import { Client, Room } from "colyseus.js";
import {
  applyAction,
  createInitialState,
  createPlayer,
  createSnapshot,
  type GameAction,
  type GameSnapshot,
  type GameState
} from "@gunnrats23/shared";

export interface GameAdapter {
  readonly kind: "solo" | "online";
  onSnapshot(listener: (snapshot: GameSnapshot) => void): void;
  dispatch(action: GameAction): void;
  disconnect(): void;
}

const saveKey = "gunnrats23:solo-state:v1";

export class SoloAdapter implements GameAdapter {
  readonly kind = "solo" as const;
  private state: GameState;
  private listeners: Array<(snapshot: GameSnapshot) => void> = [];
  private player = createPlayer("solo", "Solo Researcher", 0);

  constructor() {
    this.state = this.load() ?? createInitialState();
    queueMicrotask(() => this.emit());
  }

  onSnapshot(listener: (snapshot: GameSnapshot) => void) {
    this.listeners.push(listener);
    listener(this.snapshot());
  }

  dispatch(action: GameAction) {
    this.state = applyAction(this.state, action);
    this.persist();
    this.emit();
  }

  disconnect() {
    this.listeners = [];
  }

  private emit() {
    const snapshot = this.snapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  private snapshot() {
    return createSnapshot(this.state, [this.player], null, "Solo Campaign");
  }

  private persist() {
    window.localStorage.setItem(saveKey, JSON.stringify(this.state));
  }

  private load() {
    const raw = window.localStorage.getItem(saveKey);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as GameState;
    } catch {
      return null;
    }
  }
}

export class OnlineAdapter implements GameAdapter {
  readonly kind = "online" as const;
  private room: Room | undefined;
  private listeners: Array<(snapshot: GameSnapshot) => void> = [];

  private constructor(room: Room) {
    this.room = room;
    this.room.onMessage("snapshot", (snapshot: GameSnapshot) => {
      this.listeners.forEach((listener) => listener(snapshot));
    });
    this.room.send("requestSnapshot");
  }

  static async host(name: string) {
    const client = new Client(resolveEndpoint());
    const room = await client.create("lab", { name });
    return new OnlineAdapter(room);
  }

  static async join(name: string, roomId: string) {
    const client = new Client(resolveEndpoint());
    const room = await client.joinById(roomId.trim(), { name });
    return new OnlineAdapter(room);
  }

  onSnapshot(listener: (snapshot: GameSnapshot) => void) {
    this.listeners.push(listener);
  }

  dispatch(action: GameAction) {
    this.room?.send("action", action);
  }

  disconnect() {
    this.room?.leave();
    this.room = undefined;
    this.listeners = [];
  }
}

function resolveEndpoint() {
  const configured = import.meta.env.VITE_GAME_SERVER_URL as string | undefined;
  if (configured) {
    return configured;
  }
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return `${protocol}://${window.location.hostname}:2567`;
  }
  return `${protocol}://${window.location.host}`;
}
